import React, { useEffect, useRef } from 'react';
import { Virtuoso, VirtuosoHandle } from 'react-virtuoso';
import { cssVar, HStack, Box, Text } from '@cad-arc';
import { isString, map } from 'lodash';
import { observer } from 'mobx-react-lite';
import { formatShortTime } from '../common/helpers/utils';
import { CommandLineEditorStore } from './command-line-editor-store';
import { CliExecutionResponseTokenTypeEnum, CommandLineHistoryView } from './types';
import { commandStatusEnum } from './types/command-status-enum';
import { useOpenEventPanel } from '../event-panel/use-open-event-panel';
import { colors } from '../colors/colors';

function ErrorText(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...props}
            style={{
                fontSize: '13px',
                padding: '0 0 5px 0',
                color: cssVar('arc.colors.negative.default'),
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

function CommentText(props: React.HTMLAttributes<HTMLSpanElement>) {
    return (
        <span
            {...props}
            style={{
                fontStyle: 'italic',
                opacity: 0.7,
                ...props.style,
            }}
        >
            {props.children}
        </span>
    );
}

function EventLink(props: React.HTMLAttributes<HTMLSpanElement>) {
    return (
        <span
            {...props}
            style={{
                cursor: 'pointer',
                textDecoration: 'underline',
                ...props.style,
            }}
        >
            {props.children}
        </span>
    );
}

function RunningText(props: React.HTMLAttributes<HTMLDivElement>) {
    return (
        <div
            {...props}
            style={{
                display: 'flex',
                fontSize: '13px',
                padding: '0 0 5px 0',
                color: 'var(--arc-colors-warning-hover)',
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

function SuccessText(props: React.HTMLAttributes<HTMLDivElement> & { partial?: boolean }) {
    return (
        <div
            {...props}
            style={{
                fontSize: '13px',
                padding: '0 0 5px 0',
                color: props.partial ? colors.commandLineHistoryPartialSuccess : 'var(--arc-colors-positive-default)',
                ...props.style,
            }}
        >
            {props.children}
        </div>
    );
}

const SuccessTextDisplay = observer(function SuccessTextDisplay({
    item,
}: {
    item: CommandLineHistoryView;
}): React.ReactElement {
    const openEventPanel = useOpenEventPanel();
    const partial = !!item.partialSuccessMessage;
    const message = isString(item.successText)
        ? item.successText
        : map(item.successText, (part) => {
              switch (part.type) {
                  case CliExecutionResponseTokenTypeEnum.COMMENT.name:
                      return <CommentText key={part.text}> {part.text} </CommentText>;
                  case CliExecutionResponseTokenTypeEnum.UNIT_STATE.name:
                      return <span key={part.text}>{part.data.displayValue}</span>;
                  case CliExecutionResponseTokenTypeEnum.AGENCY_EVENT.name: {
                      const eventId = part.data.id;
                      const eventNumber = part.data.agencyEventNumber;
                      return (
                          <span
                              onClick={() => {
                                  openEventPanel({ eventId, agencyEventNumber: eventNumber });
                              }}
                              key={eventId}
                          >
                              <EventLink>{eventNumber} </EventLink>
                          </span>
                      );
                  }
                  case CliExecutionResponseTokenTypeEnum.UNIT.name:
                      return <span key={part.text}>{`Unit ${part.data.callSign}`}</span>;
                  default:
                      return `${part.text} `;
              }
          });

    return (
        <>
            <SuccessText partial={partial}>
                <span>
                    {partial ? 'Partial Success: ' : null}
                    {message}
                </span>
            </SuccessText>

            {item.partialSuccessMessage ? (
                <SuccessText partial={true}>{item.partialSuccessMessage}</SuccessText>
            ) : null}
        </>
    );
});

function SecondLineForCommand({
    item,
}: {
    item: CommandLineHistoryView;
}): React.ReactElement | null {
    switch (item.status) {
        case commandStatusEnum.SUCCESS:
            return <SuccessTextDisplay item={item} />;
        case commandStatusEnum.PARTIAL_SUCCESS:
            return <SuccessTextDisplay item={item} />;
        case commandStatusEnum.RUNNING:
            return <RunningText>Executing...</RunningText>;
        case commandStatusEnum.ERROR:
            return <ErrorText>Error: {item.errors}</ErrorText>;
        default:
            return null;
    }
}

export const CommandLineHistoryViewDisplay = observer(
    function CommandLineHistoryViewDisplay(props: {
        item: CommandLineHistoryView;
    }): React.ReactElement {
        const { item } = props;
        return (
            <HStack
                fontFamily="roboto-mono"
                alignItems="flex-start"
                w="full"
                userSelect="text"
                bg={item.status === commandStatusEnum.ERROR ? `negative.accent` : 'transparent'}
            >
                <Box w="60px" px="2">
                    <Text color="secondary" style={{ fontSize: '13px' }}>
                        {item.startTime ? formatShortTime(item.startTime) : ''}
                    </Text>
                </Box>
                <Box px="2" flexGrow={1} data-testid="COMMAND_LINE_HISTORY">
                    <Text color="primary" style={{ fontSize: '13px', padding: '0 0 5px 0' }}>
                        {item.commandText}
                    </Text>
                    <SecondLineForCommand item={item} />
                </Box>
            </HStack>
        );
    }
);

export const CommandLineHistoryList = observer(function CommandLineHistoryList(props: {
    store: CommandLineEditorStore;
    historyRef: (element?: HTMLElement | null | undefined) => void;
}): React.ReactElement {
    const { store, historyRef } = props;
    const rows = store.visibleItems.slice();

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    const renderItemContent = (index: number): React.ReactElement => {
        const item = rows[index];
        return <CommandLineHistoryViewDisplay item={item} />;
    };

    const totalCount = rows.length;

    useEffect(() => {
        if (virtuosoRef?.current) {
            setTimeout(() => {
                virtuosoRef?.current?.scrollToIndex({
                    index: 'LAST',
                    align: 'end',
                    behavior: 'auto',
                });
            }, 0);
        }
    }, [totalCount, virtuosoRef]);
    return (
        <Box h="full" className="commmand-line-history-list" ref={historyRef}>
            <Virtuoso
                ref={virtuosoRef}
                totalCount={totalCount}
                overscan={10}
                itemContent={renderItemContent}
                initialTopMostItemIndex={totalCount - 1}
            />
        </Box>
    );
});

export function CommandLineHistory({
    editorStore,
    historyRef,
}: {
    editorStore: CommandLineEditorStore;
    historyRef: (element?: HTMLElement | null | undefined) => void;
}): React.ReactElement {
    return <CommandLineHistoryList historyRef={historyRef} store={editorStore} />;
}
